import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiShoppingBag, FiLayout, FiSmartphone, FiTruck, FiBarChart2, FiGlobe, FiCheck, FiArrowRight, FiMenu, FiX, FiPrinter, FiUsers, FiPackage, FiCreditCard, FiWifi, FiMapPin } from 'react-icons/fi';
import { t as tr, Lang } from '../utils/translations';
import './LandingPage.css';

export default function LandingPage() {
  const [mobileMenu, setMobileMenu] = useState(false);
  const [lang, setLang] = useState<Lang>(() => (localStorage.getItem('cartz_lang') as Lang) || 'en');

  const t = (key: string) => tr(key, lang);
  const isRTL = lang === 'ar';

  useEffect(() => {
    const id = 'tawk-landing';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = 'https://embed.tawk.to/69cd2b936272c91c348cc025/1jl4n4eau';
    s.setAttribute('crossorigin', '*');
    document.head.appendChild(s);

    const tintLauncher = () => {
      document.querySelectorAll<HTMLIFrameElement>('iframe').forEach((frame) => {
        const style = frame.getAttribute('style') || '';
        if (style.includes('min-width:64px') && style.includes('max-height:60px') && !style.includes('hue-rotate')) {
          frame.style.setProperty('filter', 'hue-rotate(235deg) saturate(.85)', 'important');
        }
      });
    };
    const widgetObserver = new MutationObserver(tintLauncher);
    widgetObserver.observe(document.body, { childList: true, attributes: true, subtree: true });
    tintLauncher();

    return () => {
      widgetObserver.disconnect();
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, []);

  const features = [
    { icon: <FiShoppingBag />, title: t('feat.store'), desc: t('feat.store.desc') },
    { icon: <FiLayout />, title: t('feat.layouts'), desc: t('feat.layouts.desc') },
    { icon: <FiSmartphone />, title: t('feat.mobile'), desc: t('feat.mobile.desc') },
    { icon: <FiTruck />, title: t('feat.delivery'), desc: t('feat.delivery.desc') },
    { icon: <FiBarChart2 />, title: t('feat.analytics'), desc: t('feat.analytics.desc') },
    { icon: <FiGlobe />, title: t('feat.domain'), desc: t('feat.domain.desc') },
  ];

  const pricingPlans = [
    {
      key: 'basic',
      name: lang === 'ar' ? 'البداية' : 'Starter',
      price: '99',
      monthly: '9.9',
      period: lang === 'ar' ? '/سنة' : '/year',
      trial: lang === 'ar' ? '15 يوم تجربة مجانية' : '15 days free trial',
      features: lang === 'ar'
        ? ['حتى 100 منتج', 'متجر إلكتروني واحد', 'عربي وإنجليزي', 'نطاق فرعي مجاني', 'الطلبات والمخزون', 'الدفع والتوصيل', 'بدون عمولة على المبيعات']
        : ['Up to 100 products', '1 online store', 'Arabic & English', 'Free Cartz subdomain', 'Orders & inventory', 'Payments & delivery', '0% sales commission'],
      cta: lang === 'ar' ? 'ابدأ التجربة المجانية' : 'Start Free Trial',
      popular: false,
    },
    {
      key: 'professional',
      name: lang === 'ar' ? 'النمو' : 'Growth',
      price: '199',
      monthly: '19.9',
      period: lang === 'ar' ? '/سنة' : '/year',
      trial: lang === 'ar' ? '15 يوم تجربة مجانية' : '15 days free trial',
      features: lang === 'ar'
        ? ['منتجات غير محدودة', 'نطاق مخصص', 'نقطة بيع POS', 'حتى 3 فروع', 'إدارة الفريق والصلاحيات', 'تقارير متقدمة', 'دعم أولوية']
        : ['Unlimited products', 'Custom domain', 'Cartz POS included', 'Up to 3 branches', 'Team roles & permissions', 'Advanced reports', 'Priority support'],
      cta: lang === 'ar' ? 'ابدأ التجربة المجانية' : 'Start Free Trial',
      popular: true,
    },
    {
      key: 'enterprise',
      name: lang === 'ar' ? 'متعدد القنوات' : 'Omnichannel',
      price: '299',
      monthly: '29.9',
      period: lang === 'ar' ? '/سنة' : '/year',
      trial: lang === 'ar' ? '15 يوم تجربة مجانية' : '15 days free trial',
      features: lang === 'ar'
        ? ['كل مزايا النمو', 'حتى 3 متاجر', 'فروع غير محدودة', 'POS للويب وiOS وAndroid', 'مخزون موحد للمتجر والفروع', 'واجهة API وتكاملات', 'دعم مخصص']
        : ['Everything in Growth', 'Up to 3 stores', 'Unlimited branches', 'Web, iOS & Android POS', 'Unified online & branch stock', 'API & integrations', 'Dedicated support'],
      cta: lang === 'ar' ? 'ابدأ التجربة المجانية' : 'Start Free Trial',
      popular: false,
    },
  ];

  const layoutNames: Record<string, Record<Lang, string>> = {
    Modern: { en: 'Modern', ar: 'عصري' },
    Classic: { en: 'Classic', ar: 'كلاسيكي' },
    Minimal: { en: 'Minimal', ar: 'بسيط' },
    Elegant: { en: 'Elegant', ar: 'أنيق' },
  };

  return (
    <div className={`landing ${isRTL ? 'rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      <Helmet>
        <title>{lang === 'ar' ? 'كارتز - أنشئ متجرك الإلكتروني في دقائق' : 'Cartz - Create Your Online Store in Minutes'}</title>
        <meta name="description" content={lang === 'ar' ? 'أطلق متجرك الإلكتروني مع كارتز. تصاميم جميلة، إدارة سهلة، إعداد سريع.' : 'Launch your online store with Cartz. Beautiful designs, easy management, fast setup.'} />
        <html lang={lang} dir={isRTL ? 'rtl' : 'ltr'} />
      </Helmet>

      {/* Navbar */}
      <nav className="landing-nav">
        <div className="container landing-nav-inner">
          <Link to="/" className="landing-logo">
            <img className="cartz-mark" src="/favicon.svg" alt="" />
            <span className="logo-text">Cartz</span>
          </Link>
          <div className={`landing-nav-links ${mobileMenu ? 'active' : ''}`}>
            <a href="#features">{t('nav.features')}</a>
            <a href="#pos">POS</a>
            <a href="#pricing">{t('nav.pricing')}</a>
            <a href="#how-it-works">{t('nav.how')}</a>
            <button className="sf-lang-toggle" onClick={() => { const newLang = lang === 'en' ? 'ar' : 'en'; setLang(newLang); localStorage.setItem('cartz_lang', newLang); }}>
              {lang === 'en' ? 'عربي' : 'English'}
            </button>
            <Link to="/login" className="btn btn-outline btn-sm">{t('nav.login')}</Link>
            <Link to="/register" className="btn btn-primary btn-sm">{t('nav.create')}</Link>
          </div>
          <button className="mobile-menu-btn" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <FiX /> : <FiMenu />}
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="hero">
        <div className="hero-bg" />
        <div className="container hero-grid">
          <div className="hero-content">
            <div className="hero-badge">{lang === 'ar' ? 'التجارة ونقاط البيع — منصة واحدة' : 'Commerce + POS, one platform'}</div>
            <h1 className="hero-title">
              {lang === 'ar' ? 'بع في متجرك، فرعك،' : 'Sell online, in-store,'}<br />
              <span className="gradient-text">{lang === 'ar' ? 'ومن أي مكان.' : 'and everywhere between.'}</span>
            </h1>
            <p className="hero-subtitle">
              {lang === 'ar' ? 'كارتز يجمع المتجر الإلكتروني ونقاط البيع والمخزون والعملاء والفروع في نظام واحد مصمم للسوق الكويتي.' : 'Cartz brings your online store, POS, inventory, customers, and branches together in one system built for Kuwait.'}
            </p>
            <div className="hero-actions">
              <Link to="/register" className="btn btn-primary btn-lg">
                {lang === 'ar' ? 'ابدأ مجاناً لمدة 15 يوم' : 'Start 15 days free'} <FiArrowRight />
              </Link>
              <a href="#pos" className="btn btn-white btn-lg">{lang === 'ar' ? 'شاهد نقطة البيع' : 'Explore the POS'}</a>
            </div>
            <div className="hero-trust-row">
              <span><FiCheck /> {lang === 'ar' ? 'بدون عمولة' : '0% commission'}</span>
              <span><FiCheck /> KNET</span>
              <span><FiCheck /> {lang === 'ar' ? 'عربي + إنجليزي' : 'Arabic + English'}</span>
            </div>
          </div>
          <div className="hero-device-wrap" aria-label={lang === 'ar' ? 'نقطة بيع كارتز على جهاز لوحي' : 'Cartz POS on a tablet'}>
            <div className="device-glow" />
            <div className="tablet-device">
              <div className="tablet-camera" />
              <div className="tablet-screen">
                <img src="/images/pos-screen-tablet.jpg" alt={lang === 'ar' ? 'واجهة نقطة بيع كارتز الحقيقية' : 'Real Cartz point of sale interface'} />
              </div>
            </div>
            <div className="floating-proof proof-orders"><FiBarChart2 /><span><strong>50K+</strong>{lang === 'ar' ? ' طلب مُدار' : ' orders managed'}</span></div>
            <div className="floating-proof proof-sync"><FiWifi /><span><strong>{lang === 'ar' ? 'مزامنة مباشرة' : 'Live sync'}</strong>{lang === 'ar' ? ' لكل الفروع' : ' across branches'}</span></div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="features-section" id="features">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">{t('nav.features')}</span>
            <h2>{t('features.title')}</h2>
            <p>{t('features.subtitle')}</p>
          </div>
          <div className="features-grid">
            {features.map((f, i) => (
              <div key={i} className="feature-card animate-fade-in" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POS showcase */}
      <section className="pos-section" id="pos">
        <div className="container pos-grid">
          <div className="pos-visual">
            <div className="pos-tablet-secondary">
              <img src="/images/pos-screen-tablet.jpg" alt={lang === 'ar' ? 'شاشة البيع والمخزون في كارتز' : 'Cartz sales and inventory screen'} />
            </div>
          </div>
          <div className="pos-copy">
            <span className="section-badge">Cartz POS</span>
            <h2>{lang === 'ar' ? 'نقطة بيع سريعة، متصلة، وجاهزة لكل فرع' : 'A faster counter, connected to every channel'}</h2>
            <p>{lang === 'ar' ? 'استخدم نقطة البيع من الويب أو تطبيقات iOS وAndroid، مع مزامنة فورية للمخزون والطلبات والعملاء.' : 'Run Cartz POS on the web, iOS, or Android with real-time inventory, order, and customer sync.'}</p>
            <div className="pos-feature-list">
              {[
                [<FiPackage />, lang === 'ar' ? 'مخزون موحد' : 'Unified inventory', lang === 'ar' ? 'مخزون الفروع والمتجر الإلكتروني في عرض واحد.' : 'Branch and online stock in one clear view.'],
                [<FiPrinter />, lang === 'ar' ? 'طباعة مرنة' : 'Flexible printing', lang === 'ar' ? 'طابعات حرارية 58/80 مم والطابعات العادية.' : '58/80mm thermal and standard printers.'],
                [<FiUsers />, lang === 'ar' ? 'عملاء وصلاحيات' : 'Customers & permissions', lang === 'ar' ? 'إضافة عميل بسرعة وتحديد الفروع لكل موظف.' : 'Quick customer add and branch access per team member.'],
                [<FiCreditCard />, lang === 'ar' ? 'دفع وفواتير' : 'Payments & invoices', lang === 'ar' ? 'نقدي وبطاقة وإرسال الفاتورة بالبريد.' : 'Cash, card, refunds, and invoices by email.'],
                [<FiMapPin />, lang === 'ar' ? 'متعدد الفروع' : 'Multi-branch', lang === 'ar' ? 'وردية وصندوق وتقارير لكل فرع.' : 'Shifts, cash drawers, and reporting by branch.'],
                [<FiWifi />, lang === 'ar' ? 'يعمل عبر الأجهزة' : 'Works everywhere', lang === 'ar' ? 'ويب وأجهزة لوحية وهواتف iOS وAndroid.' : 'Web, tablet, iOS, and Android ready.'],
              ].map(([icon, title, desc]: any, i) => (
                <div className="pos-feature" key={i}><div className="pos-feature-icon">{icon}</div><div><h3>{title}</h3><p>{desc}</p></div></div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="how-section" id="how-it-works">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">{lang === 'ar' ? 'إعداد بسيط' : 'Simple Setup'}</span>
            <h2>{t('how.title')}</h2>
          </div>
          <div className="steps-grid">
            <div className="step">
              <div className="step-number">1</div>
              <h3>{t('how.step1')}</h3>
              <p>{t('how.step1.desc')}</p>
            </div>
            <div className="step-arrow">
              <FiArrowRight style={isRTL ? { transform: 'rotate(180deg)' } : {}} />
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>{t('how.step2')}</h3>
              <p>{t('how.step2.desc')}</p>
            </div>
            <div className="step-arrow">
              <FiArrowRight style={isRTL ? { transform: 'rotate(180deg)' } : {}} />
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>{t('how.step3')}</h3>
              <p>{t('how.step3.desc')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Layouts Preview */}
      <section className="layouts-section">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">{lang === 'ar' ? 'خيارات التصميم' : 'Design Options'}</span>
            <h2>{t('layouts.title')}</h2>
            <p>{t('layouts.subtitle')}</p>
          </div>
          <div className="layouts-grid">
            {['Modern', 'Classic', 'Minimal', 'Elegant'].map((layout, i) => (
              <div key={i} className="layout-card">
                <div className={`layout-preview layout-${layout.toLowerCase()}`}>
                  <div className="lp-header" />
                  <div className="lp-nav" />
                  <div className="lp-grid">
                    <div /><div /><div />
                  </div>
                </div>
                <h4>{layoutNames[layout][lang]}</h4>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="pricing-section" id="pricing">
        <div className="container">
          <div className="section-header">
            <span className="section-badge">{t('nav.pricing')}</span>
            <h2>{t('pricing.title')}</h2>
            <p>{t('pricing.subtitle')}</p>
          </div>
          <div className="pricing-grid">
            {pricingPlans.map((plan, i) => (
              <div key={i} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
                {plan.popular && <div className="popular-badge">{lang === 'ar' ? 'الأكثر شعبية' : 'Most Popular'}</div>}
                <h3>{plan.name}</h3>
                <div className="price">
                  {plan.price !== 'Free' && plan.price !== 'مجاني' && <span className="currency">{lang === 'ar' ? 'د.ك' : 'KD'}</span>}
                  <span className="amount">{plan.price}</span>
                  {plan.period && <span className="period">{plan.period}</span>}
                </div>
                <div className="monthly-note">{lang === 'ar' ? `أو ${plan.monthly} د.ك شهرياً` : `or KD ${plan.monthly} monthly`}</div>
                {plan.trial && (
                  <div style={{ marginTop: -4, marginBottom: 12, textAlign: 'center' }}>
                    <span style={{
                      display: 'inline-block', padding: '4px 14px', borderRadius: 20,
                      background: '#dcfce7', color: '#16a34a', fontSize: 12, fontWeight: 600,
                    }}>
                      {plan.trial}
                    </span>
                  </div>
                )}
                <ul className="plan-features">
                  {plan.features.map((f, j) => (
                    <li key={j}><FiCheck /> {f}</li>
                  ))}
                </ul>
                <Link to={`/register?plan=${plan.key}`} className={`btn ${plan.popular ? 'btn-primary' : 'btn-outline'} btn-lg`} style={{ width: '100%' }}>
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>{t('cta.title')}</h2>
            <p>{t('cta.subtitle')}</p>
            <Link to="/register" className="btn btn-primary btn-lg">
              {t('cta.btn')} {!isRTL && <FiArrowRight />}
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="landing-logo">
                <img className="cartz-mark" src="/favicon.svg" alt="" />
                <span className="logo-text">Cartz</span>
              </div>
              <p>{t('footer.tagline')}</p>
            </div>
            <div className="footer-links">
              <h4>{t('footer.product')}</h4>
              <a href="#features">{t('nav.features')}</a>
              <a href="#pricing">{t('nav.pricing')}</a>
              <a href="#how-it-works">{t('nav.how')}</a>
            </div>
            <div className="footer-links">
              <h4>{t('footer.company')}</h4>
              <a href="#features">{t('footer.about')}</a>
              <a href="#pos">{t('footer.blog')}</a>
              <a href="mailto:support@cartz.cc">{t('footer.contact')}</a>
            </div>
            <div className="footer-links">
              <h4>{t('footer.legal')}</h4>
              <Link to="/privacy">{t('footer.privacy')}</Link>
              <Link to="/terms">{t('footer.terms')}</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {t('footer.rights')}</p>
            <p className="footer-operator">
              {lang === 'ar' ? 'تشغيل بواسطة ' : 'Operated by '}
              <a href="https://roolink.net" target="_blank" rel="noopener noreferrer">
                {lang === 'ar' ? 'رولنك لتصميم وتطوير التطبيقات الإلكترونية' : 'Roolink For Electronic App Designing and Development'}
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
